import { useMemo, useState } from 'react';
import { Search } from 'lucide-react';
import { Spinner } from '@/components/ui/spinner';
import { EmptyState } from '@/components/ui/empty-state';
import { ErrorMessage } from '@/components/ui/error-message';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils/cn';
import { documentClassLabel } from '@/lib/document-classes';
import {
  sharePointSourceOf,
  type ClassificationItem,
  type DocumentListItem,
} from '@/lib/api/documents';
import type { DocumentProcessingItem } from '@/lib/api/document-processing';
import type { RoutingProposal } from '@/lib/api/review-proposals';
import { FolderTree, type FolderTreeItem } from './folder-tree';
import { IngestionSection } from './ingestion-section';
import { ConnectedFolderCard } from './connected-folder';
import { DocCard } from './doc-card';

/**
 * T-0099 / C20-137 — Documents library (folder tree + ingestion + tabbed
 * document grid).
 *
 * Visual contract: `design_handoff_caliber_v2/prototype/screens-engagement.js`
 * → `Screens.documents` (the two-column layout: ~25% sources + ingestion
 * rail / ~75% tabbed grid).
 *
 * C20-137 — what changed vs the previous T-0099 implementation:
 *   - The left rail now stacks the folder list (top) with a live
 *     **Ingestion** section (bottom). The ingestion rows render real
 *     pending docs (parse / OCR / classify status) — no longer hidden
 *     behind a collapsible chevron in the AI banner only.
 *   - The right pane gains the prototype's "Recent / All / By type"
 *     tab bar. "Recent" sorts by `classified_at` desc (top 10);
 *     "All" preserves the prior ordering; "By type" groups rows by
 *     their classification and renders one section per type.
 *   - The header reads "{title} · {count}" — matches the prototype's
 *     active-tab subtitle.
 *
 * T-0160 — the grid lists every non-deleted document from the documents
 * list (`libraryItemsFor`); classifications enrich the rows. It used to list
 * classified documents only, so a document that was never read — still
 * processing, or failed — never appeared and could not be retried or
 * deleted.
 *
 * T-0211 (E18-F01) — the connected SharePoint folder: its card sits under
 * the folder tree (`ConnectedFolderCard`), the tree gains a virtual **From
 * SharePoint** folder counting the documents the folder took, and each such
 * row carries the `SharePoint` chip (`DocCard`).
 *
 * The library composes:
 *   - `FolderTree` — derived from distinct classifications (T-0044).
 *   - `IngestionSection` — per-doc parse/OCR/classify status (T-0041 /
 *     T-0044). Backend missing → renders an empty-state stub.
 *   - `DocCard` per item — the prototype's `docRow()`.
 *
 * Filter coordination:
 *   - The folder tree drives a `activeFolder` state (string id). "all" is
 *     the default and matches every document.
 *   - The search input drives a `q` state. Matching is case-insensitive
 *     against the filename.
 *   - The tab bar drives a `tab` state. Tabs are AND-combined with the
 *     folder + search filters.
 *
 * Drag-drop upload: the route attaches drag handlers; this component leaves
 * a `onUpload` callback prop wired to the right place (T-0113 will own the
 * upload-modal UI; the route picks up the drop event and opens the modal).
 */

export interface DocumentsLibraryProps {
  engagementId: string;
  /** Classifications projection — enriches the rows of classified documents. */
  items: ClassificationItem[];
  /**
   * Documents projection — every non-deleted document, one grid row each
   * (T-0160), plus the doc count + ingestion rows with progress (T-0159).
   */
  documents: DocumentProcessingItem[];
  /** AI proposals awaiting review — drives the "N proposals" per-card chip. */
  proposals: RoutingProposal[];
  /** Items not yet at a terminal classification status. */
  pendingItems: ClassificationItem[];
  isLoading: boolean;
  error: unknown;
  className?: string;
}

const ALL_FOLDER_ID = 'all';
/** The virtual folder of the documents the connected SharePoint folder took (T-0211). */
const SHAREPOINT_FOLDER_ID = 'source:sharepoint';
type GridTab = 'recent' | 'all' | 'type';
const TAB_LABELS: Record<GridTab, string> = {
  recent: 'Recent',
  all: 'All',
  type: 'By type',
};

/**
 * T-0160 — the grid's rows: one per document in the documents list, which
 * holds every non-deleted document — still uploading, being read, failed,
 * or indexed. A document's classification item, when it has one, supplies
 * its row (with the document's title); a document without one (never read,
 * or its read failed — it is never classified) gets a row built from the
 * list entry, so it is no longer invisible. Classification items of
 * documents not in the list (deleted ones) are dropped.
 */
export function libraryItemsFor(
  items: ClassificationItem[],
  documents: DocumentListItem[],
): ClassificationItem[] {
  const byDocument = new Map(items.map((it) => [it.document_id, it]));
  return documents.map((doc) => {
    const classified = byDocument.get(doc.id);
    if (classified) {
      return { ...classified, document_title: classified.document_title || doc.name };
    }
    return {
      document_id: doc.id,
      document_title: doc.name,
      status: 'unclassified',
      // The chain-head classification the list carries, when there is one.
      predicted_type: doc.classification,
      corrected_type: null,
      confidence: null,
      // Upload time: what "Recent" sorts by and the row's "uploaded" date.
      classified_at: doc.created_at,
      mime_type: doc.mime_type || null,
    };
  });
}

/**
 * Derive the folder tree from the documents list. Each distinct
 * classification (predicted or corrected) becomes a virtual folder; the
 * count is the number of documents with that classification. An explicit
 * "All documents" entry sits at the top, and "From SharePoint" (T-0211)
 * follows it when the connected folder took any of the documents.
 */
function deriveFolders(
  items: ClassificationItem[],
  documentsById: ReadonlyMap<string, DocumentListItem>,
): FolderTreeItem[] {
  const buckets = new Map<string, number>();
  let fromSharePoint = 0;
  for (const item of items) {
    const key = item.corrected_type ?? item.predicted_type ?? 'unclassified';
    buckets.set(key, (buckets.get(key) ?? 0) + 1);
    if (sharePointSourceOf(documentsById.get(item.document_id))) fromSharePoint += 1;
  }
  const entries = Array.from(buckets.entries())
    .map(([id, count]) => ({
      id,
      label: documentClassLabel(id),
      count,
    }))
    .sort((a, b) => b.count - a.count);
  return [
    { id: ALL_FOLDER_ID, label: 'All documents', count: items.length },
    ...(fromSharePoint > 0
      ? [{ id: SHAREPOINT_FOLDER_ID, label: 'From SharePoint', count: fromSharePoint }]
      : []),
    ...entries,
  ];
}

/**
 * Filter the documents by the selected folder + the search query.
 * `q` is case-insensitive substring match against the filename.
 */
function filterDocs(
  items: ClassificationItem[],
  folderId: string,
  q: string,
  documentsById: ReadonlyMap<string, DocumentListItem>,
): ClassificationItem[] {
  const needle = q.trim().toLowerCase();
  return items.filter((item) => {
    if (folderId === SHAREPOINT_FOLDER_ID) {
      if (!sharePointSourceOf(documentsById.get(item.document_id))) return false;
    } else if (folderId !== ALL_FOLDER_ID) {
      const cls = item.corrected_type ?? item.predicted_type ?? 'unclassified';
      if (cls !== folderId) return false;
    }
    if (needle.length > 0 && !item.document_title.toLowerCase().includes(needle)) return false;
    return true;
  });
}

/**
 * Apply the active tab to an already-filtered (folder + search) list.
 * "Recent" — sort by `classified_at` desc and take the top 10.
 * "All" — preserve the input order.
 * "By type" — return as-is; the renderer groups when this tab is active.
 */
function applyTab(items: ClassificationItem[], tab: GridTab): ClassificationItem[] {
  if (tab === 'recent') {
    const dated = items.map((it) => ({
      it,
      ts: it.classified_at ? new Date(it.classified_at).getTime() : 0,
    }));
    dated.sort((a, b) => b.ts - a.ts);
    return dated.slice(0, 10).map((d) => d.it);
  }
  return items;
}

/**
 * Group AI proposals by target_ref.document_id so we can render a per-card
 * "N proposals" affordance. The shape of `target_ref` is opaque (jsonb on
 * the wire) — we look for a `document_id` field as a best-effort match.
 */
function groupProposalCounts(proposals: RoutingProposal[]): Map<string, number> {
  const counts = new Map<string, number>();
  for (const p of proposals) {
    const tr = p.target_ref;
    if (tr && typeof tr === 'object') {
      const docId = (tr as Record<string, unknown>).document_id;
      if (typeof docId === 'string') {
        counts.set(docId, (counts.get(docId) ?? 0) + 1);
      }
    }
  }
  return counts;
}

/**
 * Group the displayed items by classification for the "By type" tab. The
 * grouping order mirrors `deriveFolders` (most-populated first).
 */
function groupByType(
  items: ClassificationItem[],
): Array<{ type: string; items: ClassificationItem[] }> {
  const map = new Map<string, ClassificationItem[]>();
  for (const it of items) {
    const key = it.corrected_type ?? it.predicted_type ?? 'unclassified';
    const bucket = map.get(key) ?? [];
    bucket.push(it);
    map.set(key, bucket);
  }
  return Array.from(map.entries())
    .map(([type, group]) => ({ type, items: group }))
    .sort((a, b) => b.items.length - a.items.length);
}

export function DocumentsLibrary({
  engagementId,
  items,
  documents,
  proposals,
  pendingItems,
  isLoading,
  error,
  className,
}: DocumentsLibraryProps) {
  const [activeFolder, setActiveFolder] = useState<string>(ALL_FOLDER_ID);
  const [q, setQ] = useState('');
  const [tab, setTab] = useState<GridTab>('recent');

  const rows = useMemo(() => libraryItemsFor(items, documents), [items, documents]);
  const documentsById = useMemo(() => new Map(documents.map((d) => [d.id, d])), [documents]);
  const folders = useMemo(() => deriveFolders(rows, documentsById), [rows, documentsById]);
  const filteredByFolder = useMemo(
    () => filterDocs(rows, activeFolder, q, documentsById),
    [rows, activeFolder, q, documentsById],
  );
  const filtered = useMemo(() => applyTab(filteredByFolder, tab), [filteredByFolder, tab]);
  const groupedByType = useMemo(
    () => (tab === 'type' ? groupByType(filtered) : []),
    [filtered, tab],
  );
  const proposalCounts = useMemo(() => groupProposalCounts(proposals), [proposals]);

  const headerTitle =
    activeFolder === ALL_FOLDER_ID
      ? 'All documents'
      : activeFolder === SHAREPOINT_FOLDER_ID
        ? 'From SharePoint'
        : documentClassLabel(activeFolder);

  return (
    <div
      className={cn('grid grid-cols-1 gap-4 lg:grid-cols-[280px_1fr] lg:items-stretch', className)}
    >
      {/* Left rail — folder tree + ingestion summary. */}
      <aside className="flex flex-col gap-4 rounded-card border border-ink-100 bg-paper-0 p-4">
        <div>
          <div className="t-h3 mb-3 font-semibold text-ink-900">Sources</div>
          <FolderTree items={folders} activeId={activeFolder} onSelect={setActiveFolder} />
        </div>
        {/* The connected SharePoint folder (T-0211). It draws its own rule
            above itself, since a consultant with no folder sees nothing. */}
        <ConnectedFolderCard engagementId={engagementId} />
        <hr className="border-t border-ink-100" />
        <div>
          <div className="t-h3 mb-3 font-semibold text-ink-900">Ingestion</div>
          <IngestionSection
            engagementId={engagementId}
            pendingItems={pendingItems}
            documents={documents}
          />
        </div>
      </aside>

      {/* Right pane — tabs + search + grid */}
      <section className="flex flex-col rounded-card border border-ink-100 bg-paper-0">
        <header className="flex flex-wrap items-center gap-3 border-b border-ink-100 px-5 py-3">
          <nav aria-label="Document view" className="flex items-center gap-1">
            {(['recent', 'all', 'type'] as GridTab[]).map((t) => {
              const active = t === tab;
              return (
                <button
                  key={t}
                  type="button"
                  onClick={() => setTab(t)}
                  aria-pressed={active}
                  className={cn(
                    'inline-flex h-8 items-center rounded-card px-3 text-body-sm font-medium transition-colors',
                    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-amber-600',
                    active
                      ? 'border-b-2 border-accent-amber-600 text-ink-900'
                      : 'text-ink-500 hover:text-ink-900',
                  )}
                >
                  {TAB_LABELS[t]}
                </button>
              );
            })}
          </nav>
          <div className="hidden text-meta text-ink-500 md:inline">
            {headerTitle} · {filtered.length}
          </div>
          <label className="relative ml-auto flex h-9 w-64 items-center rounded-card border border-ink-200 bg-paper-50 px-2.5 text-meta text-ink-500 focus-within:border-accent-amber-600">
            <Search className="mr-2 h-3.5 w-3.5" aria-hidden="true" />
            <Input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Filter documents…"
              className="h-7 border-0 bg-transparent p-0 text-body-sm placeholder:text-ink-300 focus-visible:ring-0"
              aria-label="Filter documents"
            />
          </label>
        </header>
        <div className="min-h-[240px]">
          {isLoading ? (
            <div className="flex items-center gap-2 p-6 text-body-sm text-ink-500">
              <Spinner /> Loading documents…
            </div>
          ) : error ? (
            <div className="p-4">
              <ErrorMessage error={error} />
            </div>
          ) : filtered.length === 0 ? (
            <div className="p-4">
              <EmptyState
                title={q.length > 0 ? 'No matching documents' : 'No documents yet'}
                description={
                  q.length > 0
                    ? 'Try a different filter or clear the search.'
                    : 'Drop a file or use the Upload button to start the pipeline.'
                }
              />
            </div>
          ) : tab === 'type' ? (
            <div>
              {groupedByType.map((group) => (
                <div key={group.type}>
                  <div className="border-b border-ink-100 bg-paper-50 px-5 py-2 text-meta font-semibold uppercase tracking-wide text-ink-500">
                    {documentClassLabel(group.type)} · {group.items.length}
                  </div>
                  <ul className="m-0 list-none p-0">
                    {group.items.map((item) => (
                      <DocCard
                        key={item.document_id}
                        engagementId={engagementId}
                        item={item}
                        document={documentsById.get(item.document_id) ?? null}
                        proposalCount={proposalCounts.get(item.document_id) ?? 0}
                      />
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          ) : (
            <ul className="m-0 list-none p-0">
              {filtered.map((item) => (
                <DocCard
                  key={item.document_id}
                  engagementId={engagementId}
                  item={item}
                  document={documentsById.get(item.document_id) ?? null}
                  proposalCount={proposalCounts.get(item.document_id) ?? 0}
                />
              ))}
            </ul>
          )}
        </div>
      </section>
    </div>
  );
}
